/**
 * PATH: packages/email-hub/src/feature/email-layout/useLayoutPopovers.ts
 * FILE_ID: EMAIL-LAYOUT-POPOVERS-001
 * PURPOSE: Own portalled menu geometry and dismissal effects for EmailLayout.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import type {
  DeepSyncMenuCoordinates,
  MailboxesMenuCoordinates,
} from './contracts'

export const MAILBOX_ACTIONS_MENU_ID = 'email-mailbox-actions-menu'

/** True when a pointer or scroll target is the mailbox dock or its portaled menu. */
export function pointerIsInsideMailboxChrome(target: Node): boolean {
  const dock = document.getElementById('email-mailboxes-dock-menu')
  const actions = document.getElementById(MAILBOX_ACTIONS_MENU_ID)
  return Boolean(dock?.contains(target) || actions?.contains(target))
}

const mailboxActionsMenuRoot = {
  get current(): HTMLElement | null {
    return document.getElementById(MAILBOX_ACTIONS_MENU_ID)
  },
}

/** Read at pointer time. A layout effect can miss the portal and leave a
 *  stale null, which makes every click inside the list look outside. */
const mailboxDockRoot = {
  get current(): HTMLElement | null {
    return document.getElementById('email-mailboxes-dock-menu')
  },
}

export function useLayoutPopovers() {
  // Deep-sync menu state. The menu portals to body because the sidebar
  // footer is inside overflow-hidden layout containers.
  const [deepSyncOpen, setDeepSyncOpen] = useState(false)
  const deepSyncRef = useRef<HTMLDivElement>(null)
  const deepSyncMenuRef = useRef<HTMLDivElement>(null)
  const [deepSyncCoords, setDeepSyncCoords] =
    useState<DeepSyncMenuCoordinates | null>(null)
  const recomputeDeepSyncCoords = useCallback(() => {
    const element = deepSyncRef.current
    if (!element) {
      setDeepSyncCoords(null)
      return
    }
    const rect = element.getBoundingClientRect()
    setDeepSyncCoords({
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + 4,
    })
  }, [])
  useEffect(() => {
    if (!deepSyncOpen) return
    recomputeDeepSyncCoords()
    window.addEventListener('resize', recomputeDeepSyncCoords)
    window.addEventListener('scroll', recomputeDeepSyncCoords, true)
    return () => {
      window.removeEventListener('resize', recomputeDeepSyncCoords)
      window.removeEventListener('scroll', recomputeDeepSyncCoords, true)
    }
  }, [deepSyncOpen, recomputeDeepSyncCoords])
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(deepSyncOpen, () => setDeepSyncOpen(false), [deepSyncRef, deepSyncMenuRef])

  // Bottom-dock / top-strip mailbox popover. Placement is trigger-aware:
  // bottom triggers open upward and top-strip triggers open downward.
  const [mailboxesDockOpen, setMailboxesDockOpen] = useState(false)
  const mailboxesBtnRef = useRef<HTMLButtonElement>(null)
  const [mailboxesCoords, setMailboxesCoords] =
    useState<MailboxesMenuCoordinates | null>(null)
  const recomputeMailboxesCoords = useCallback(() => {
    const element = mailboxesBtnRef.current
    if (!element) return
    const rect = element.getBoundingClientRect()
    const right = Math.max(8, window.innerWidth - rect.right)
    if (rect.top < window.innerHeight / 2) {
      setMailboxesCoords({ right, top: rect.bottom + 6 })
    } else {
      setMailboxesCoords({ right, bottom: window.innerHeight - rect.top + 6 })
    }
  }, [])
  useEffect(() => {
    if (!mailboxesDockOpen) return
    recomputeMailboxesCoords()
    const onResize = () => setMailboxesDockOpen(false)
    const onScroll = (event: Event) => {
      const target = event.target
      if (
        target === document
        || target === document.documentElement
        || target === document.body
      ) {
        setMailboxesDockOpen(false)
      }
    }
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [mailboxesDockOpen, recomputeMailboxesCoords])
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(
    mailboxesDockOpen,
    () => setMailboxesDockOpen(false),
    [mailboxesBtnRef, mailboxDockRoot, mailboxActionsMenuRoot],
  )

  return {
    deepSyncOpen,
    setDeepSyncOpen,
    deepSyncRef,
    deepSyncMenuRef,
    deepSyncCoords,
    mailboxesDockOpen,
    setMailboxesDockOpen,
    mailboxesBtnRef,
    mailboxesCoords,
  }
}
